import React, { useEffect, useState, useCallback } from 'react';
import {
  Button, Table, Form, Card, Spinner, Modal, Badge,
} from 'react-bootstrap';
import dayjs from 'dayjs';
import axios from 'axios';
import VisitorTrackingService from '../../Services/VisitorTrackingService';
import Error from '../../Components/Error';
import { formatDate } from '../../Utils/timezone';
import thousandSeparator from '../../Utils/numberFormat';
import { getToken } from '../../Services/BearerToken';

// Tracking tokens are generated client-side with crypto.randomUUID()
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function VisitorTracking() {
  // State for tracking list
  const [trackingRecords, setTrackingRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastKey, setLastKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // State for filters
  const [startDate, setStartDate] = useState(
    formatDate(dayjs().subtract(30, 'day').startOf('day')),
  );
  const [endDate, setEndDate] = useState(formatDate(dayjs().endOf('day')));
  const [searchToken, setSearchToken] = useState('');

  // State for bookings modal
  const [showBookingsModal, setShowBookingsModal] = useState(false);
  const [selectedTracking, setSelectedTracking] = useState(null);
  const [trackingBookings, setTrackingBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  // State for CSV export
  const [isExporting, setIsExporting] = useState(false);

  // State for token copy feedback
  const [copiedToken, setCopiedToken] = useState('');

  // State for manual association
  const [associateBookingIds, setAssociateBookingIds] = useState('');
  const [isAssociating, setIsAssociating] = useState(false);
  const [associateResult, setAssociateResult] = useState(null);

  // Fetch tracking records
  const fetchTrackingRecords = useCallback(async (reset = true) => {
    setLoading(true);
    setError('');

    try {
      const filters = {
        startDate: `${startDate}T00:00:00.000Z`,
        endDate: `${endDate}T23:59:59.999Z`,
        limit: 50,
      };

      if (!reset && lastKey) {
        filters.lastKey = lastKey;
      }

      const response = await VisitorTrackingService.list(filters);

      if (reset) {
        setTrackingRecords(response.data.items || []);
      } else {
        setTrackingRecords((prev) => [...prev, ...(response.data.items || [])]);
      }

      setLastKey(response.data.lastKey || null);
      setHasMore(!!response.data.lastKey);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching tracking records:', err);
      setError(err.response?.data?.message || 'Failed to fetch tracking records');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, lastKey]);

  // Initial load
  useEffect(() => {
    fetchTrackingRecords(true);
  }, []);

  // Fetch bookings for a tracking record
  const handleShowBookings = async (tracking) => {
    setSelectedTracking(tracking);
    setShowBookingsModal(true);
    setLoadingBookings(true);
    setTrackingBookings([]);
    setAssociateBookingIds('');
    setAssociateResult(null);

    try {
      const response = await VisitorTrackingService.getBookings(tracking.token);
      setTrackingBookings(response.data.bookings || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching bookings:', err);
      setError(err.response?.data?.message || 'Failed to fetch bookings');
    } finally {
      setLoadingBookings(false);
    }
  };

  // Handle manual association of booking IDs
  const handleAssociateBookings = async () => {
    if (!selectedTracking || !associateBookingIds.trim()) return;

    // Parse booking IDs (comma or newline separated)
    const bookingIds = associateBookingIds
      .split(/[,\n]/)
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    if (bookingIds.length === 0) {
      setAssociateResult({ errors: ['No valid booking IDs provided'] });
      return;
    }

    setIsAssociating(true);
    setAssociateResult(null);

    try {
      const result = await VisitorTrackingService.associateBookings(
        selectedTracking.token,
        bookingIds,
      );
      setAssociateResult(result.data || result);

      // Refresh bookings list after association
      if (result.data?.updated > 0 || result.updated > 0) {
        const response = await VisitorTrackingService.getBookings(selectedTracking.token);
        setTrackingBookings(response.data.bookings || []);
        setAssociateBookingIds('');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error associating bookings:', err);
      setAssociateResult({ errors: [err.response?.data?.message || 'Failed to associate bookings'] });
    } finally {
      setIsAssociating(false);
    }
  };

  // Export CSV
  const handleExportCSV = async () => {
    setIsExporting(true);
    setError('');

    try {
      const token = getToken();
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/tracking/admin/export?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        },
      );

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `visitor-tracking-export-${dayjs().format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error exporting CSV:', err);
      setError('Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  // Apply filters
  const handleApplyFilters = () => {
    setSearchToken('');
    setLastKey(null);
    fetchTrackingRecords(true);
  };

  // Search by token
  const handleSearchByToken = async () => {
    // The token is the DynamoDB hash key, so the lookup is exact: a pasted value
    // carrying quotes, stray whitespace or a trailing ellipsis never matches.
    const typed = searchToken
      .trim()
      .replace(/^["'`]+|["'`]+$/g, '')
      .replace(/(\.{3}|\u2026)+$/, '')
      .trim();

    if (!typed) return;

    // A partial token can still be resolved when the full record is on screen.
    let token = typed;
    if (!UUID_REGEX.test(token)) {
      const matches = trackingRecords.filter((r) => r.token && r.token.startsWith(token));
      if (matches.length === 1) {
        token = matches[0].token;
      } else if (matches.length > 1) {
        setError(`"${typed}" matches ${matches.length} tokens in the list below - paste the full token.`);
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const response = await VisitorTrackingService.getBookings(token);
      if (response.data?.tracking) {
        setTrackingRecords([response.data.tracking]);
        setHasMore(false);
        setLastKey(null);
      } else {
        setTrackingRecords([]);
        setError(
          UUID_REGEX.test(token)
            ? `No tracking record found for token ${token}`
            : `No tracking record found for "${typed}". Tracking tokens are full UUIDs - use the copy button in the Token column to copy the whole value.`,
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error searching by token:', err);
      setError(err.response?.data?.message || 'Failed to search by token');
      setTrackingRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Copy a full token to the clipboard
  const handleCopyToken = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(''), 1500);
    } catch {
      // Clipboard unavailable (non-HTTPS origin) - the token stays selectable anyway.
    }
  };

  // Format tracking data for display
  const formatTrackingData = (data) => {
    if (!data || Object.keys(data).length === 0) {
      return <span className="text-muted">No data</span>;
    }

    return (
      <pre
        style={{
          fontSize: '11px',
          maxHeight: '150px',
          overflow: 'auto',
          margin: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  };

  // Get status badge for booking
  const getStatusBadge = (status) => {
    const variants = {
      Confirmed: 'success',
      Pending: 'warning',
      Cancelled: 'danger',
      Completed: 'info',
      'Passenger No-Show': 'secondary',
      'Driver No-Show': 'secondary',
    };

    return (
      <Badge bg={variants[status] || 'secondary'}>
        {status}
      </Badge>
    );
  };

  return (
    <div className="container-fluid">
      <h2 className="mb-4">Visitor Tracking</h2>

      {error && <Error error={error} />}

      {/* Filters Card */}
      <Card className="mb-4">
        <Card.Body>
          <Form>
            {/* Search by Token Row */}
            <div className="row g-3 align-items-end mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Search by Token</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter full tracking token (e.g. 3f0c8a12-4b7e-4c19-9a2d-5e6f70b1c8d4)"
                    value={searchToken}
                    onChange={(e) => setSearchToken(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearchByToken())}
                  />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Button
                  variant="info"
                  onClick={handleSearchByToken}
                  disabled={loading || !searchToken.trim()}
                >
                  <i className="fa fa-search me-2" />
                  Search Token
                </Button>
              </div>
              <div className="col-md-3 text-end">
                <Button
                  variant="success"
                  onClick={handleExportCSV}
                  disabled={isExporting}
                >
                  {isExporting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Exporting...
                    </>
                  ) : 'Export CSV (Confirmed Bookings)'}
                </Button>
              </div>
            </div>

            <hr className="my-3" />

            {/* Date Filter Row */}
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Button
                  variant="primary"
                  onClick={handleApplyFilters}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Apply Date Filters'}
                </Button>
              </div>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Tracking Records Table */}
      <Card>
        <Card.Body>
          <div className="table-responsive">
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th style={{ width: '150px' }}>First Touch</th>
                  <th style={{ width: '200px' }}>Token</th>
                  <th style={{ width: '150px' }}>Phone</th>
                  <th style={{ width: '200px' }}>Email</th>
                  <th>Events</th>
                  <th>Tracking Data</th>
                  <th style={{ width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && trackingRecords.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <Spinner animation="border" />
                    </td>
                  </tr>
                )}
                {!loading && trackingRecords.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      No tracking records found
                    </td>
                  </tr>
                )}
                {trackingRecords.length > 0 && [...trackingRecords]
                  .sort((a, b) => {
                    const dateA = a.firstTouchAt ? new Date(a.firstTouchAt).getTime() : 0;
                    const dateB = b.firstTouchAt ? new Date(b.firstTouchAt).getTime() : 0;
                    return dateB - dateA;
                  })
                  .map((record) => (
                    <tr key={record.token}>
                      <td>
                        {record.firstTouchAt
                          ? dayjs(record.firstTouchAt).format('YYYY-MM-DD HH:mm')
                          : '-'}
                      </td>
                      <td>
                        <code
                          style={{ fontSize: '11px', wordBreak: 'break-all' }}
                          title={record.token}
                        >
                          {record.token || '-'}
                        </code>
                        {record.token && (
                          <Button
                            size="sm"
                            variant="link"
                            className="p-0 ms-1 align-baseline"
                            style={{ fontSize: '11px' }}
                            onClick={() => handleCopyToken(record.token)}
                          >
                            {copiedToken === record.token ? 'Copied' : 'Copy'}
                          </Button>
                        )}
                      </td>
                      <td>{record.phone || '-'}</td>
                      <td>{record.email || '-'}</td>
                      <td>
                        {record.events && record.events.length > 0 ? (
                          <ul className="list-unstyled mb-0" style={{ fontSize: '11px' }}>
                            {record.events.map((evt, idx) => (
                              <li key={`evt-${idx + 1}`}>
                                <Badge bg="info" className="me-1">{evt.type}</Badge>
                                {dayjs(evt.at).format('MM-DD HH:mm')}
                              </li>
                            ))}
                          </ul>
                        ) : '-'}
                      </td>
                      <td>{formatTrackingData(record.data)}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleShowBookings(record)}
                        >
                          View Bookings
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="text-center mt-3">
              <Button
                variant="outline-secondary"
                onClick={() => fetchTrackingRecords(false)}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Bookings Modal */}
      <Modal
        show={showBookingsModal}
        onHide={() => setShowBookingsModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Bookings for Tracking Record
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Tracking Info */}
          {selectedTracking && (
            <Card className="mb-3 bg-light">
              <Card.Body>
                <div className="row">
                  <div className="col-md-4">
                    <strong>Token:</strong>
                    <br />
                    <code style={{ fontSize: '11px' }}>{selectedTracking.token}</code>
                  </div>
                  <div className="col-md-4">
                    <strong>Phone:</strong>
                    {' '}
                    {selectedTracking.phone || '-'}
                    <br />
                    <strong>Email:</strong>
                    {' '}
                    {selectedTracking.email || '-'}
                  </div>
                  <div className="col-md-4">
                    <strong>GCLID:</strong>
                    {' '}
                    {selectedTracking.data?.gclid || '-'}
                    <br />
                    <strong>First Touch:</strong>
                    {' '}
                    {selectedTracking.firstTouchAt
                      ? dayjs(selectedTracking.firstTouchAt).format('YYYY-MM-DD HH:mm')
                      : '-'}
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Bookings Table */}
          {loadingBookings && (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          )}
          {!loadingBookings && trackingBookings.length === 0 && (
            <div className="text-center py-4 text-muted">
              No bookings found for this tracking record
            </div>
          )}
          {!loadingBookings && trackingBookings.length > 0 && (
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Route</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {trackingBookings.map((booking) => (
                    <tr key={booking.booking_reference}>
                      <td>
                        <strong>{booking.booking_reference}</strong>
                      </td>
                      <td>
                        {booking.date
                          ? dayjs(booking.date).format('YYYY-MM-DD')
                          : '-'}
                      </td>
                      <td>
                        {booking.full_name}
                        <br />
                        <small className="text-muted">{booking.email_address}</small>
                      </td>
                      <td>
                        <small>
                          {booking.destination_from?.address?.substring(0, 30)}
                          <br />
                          {booking.destination_to?.address?.substring(0, 30)}
                        </small>
                      </td>
                      <td>
                        {thousandSeparator(booking.total_price)}
                        {' '}
                        {booking.currency}
                      </td>
                      <td>{getStatusBadge(booking.status)}</td>
                      <td>
                        {booking.created_at
                          ? dayjs(booking.created_at).format('YYYY-MM-DD HH:mm')
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Summary */}
              <Card className="mt-3 bg-light">
                <Card.Body>
                  <div className="row">
                    <div className="col-md-4">
                      <strong>Total Bookings:</strong>
                      {' '}
                      {trackingBookings.length}
                    </div>
                    <div className="col-md-4">
                      <strong>Confirmed:</strong>
                      {' '}
                      {trackingBookings.filter((b) => b.status === 'Confirmed').length}
                    </div>
                    <div className="col-md-4">
                      <strong>Total Value (Confirmed):</strong>
                      {' '}
                      {thousandSeparator(
                        trackingBookings
                          .filter((b) => b.status === 'Confirmed')
                          .reduce((sum, b) => sum + (b.total_price || 0), 0),
                      )}
                      {' '}
                      EUR
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}
          {/* Manual Association Section */}
          <Card className="mt-4 border-warning">
            <Card.Header className="bg-warning-subtle">
              <strong>
                <i className="fa fa-link me-2" />
                Manual Association
              </strong>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-2">
                If a booking wasn&apos;t automatically matched to this tracking record,
                you can manually associate it by entering the booking ID(s) below.
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Booking IDs or References (comma or newline separated)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="e.g., PU_DASH_1234567, PU_DASH_7654321"
                  value={associateBookingIds}
                  onChange={(e) => setAssociateBookingIds(e.target.value)}
                  disabled={isAssociating}
                />
              </Form.Group>
              <Button
                variant="warning"
                onClick={handleAssociateBookings}
                disabled={isAssociating || !associateBookingIds.trim()}
              >
                {isAssociating ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Associating...
                  </>
                ) : (
                  <>
                    <i className="fa fa-link me-2" />
                    Associate Bookings
                  </>
                )}
              </Button>

              {/* Association Result */}
              {associateResult && (
                <div className="mt-3">
                  {associateResult.updated > 0 && (
                    <div className="alert alert-success py-2">
                      <i className="fa fa-check-circle me-2" />
                      Successfully associated
                      {' '}
                      {associateResult.updated}
                      {' '}
                      booking(s)
                    </div>
                  )}
                  {associateResult.errors && associateResult.errors.length > 0 && (
                    <div className="alert alert-danger py-2">
                      <i className="fa fa-exclamation-circle me-2" />
                      <strong>Errors:</strong>
                      <ul className="mb-0 mt-1">
                        {associateResult.errors.map((err, idx) => (
                          <li key={`err-${idx + 1}`}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBookingsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default VisitorTracking;
